import { useEffect, useRef } from 'react';

/**
 * Detecta lecturas de un lector de código de barras (que funciona como un
 * "teclado" que tipea muy rápido) y dispara `onScan`.
 *
 * Cierra la lectura de tres maneras (para funcionar con cualquier lector):
 * - Enter o Tab al final (sufijo típico), o
 * - por PAUSA: cuando termina la ráfaga rápida y no llega otra tecla en un
 *   ratito (sirve para lectores SIN sufijo configurado).
 *
 * Distingue el lector del tipeo humano por la VELOCIDAD (promedio de ms por
 * caracter). Un lector escribe a decenas de ms; un humano, > 80-100ms. Si
 * escribís a mano, nunca se dispara. No hace preventDefault sobre el tipeo
 * humano. Es opt-in: solo corre en las pantallas que lo montan.
 */
interface Options {
  enabled?: boolean;
  /** Máximo promedio de ms por caracter para considerarlo lector (default 60ms). */
  maxIntervalMs?: number;
  /** Largo mínimo del código para aceptarlo (default 3). */
  minLength?: number;
  /** Ms de pausa tras la ráfaga para cerrar la lectura sin Enter (default 120ms). */
  flushTimeoutMs?: number;
  /**
   * Si es true, evita que los caracteres de la ráfaga del lector caigan en el
   * campo que esté enfocado (preventDefault). Útil dentro de un formulario
   * donde querés capturar el código sin pararte en un campo específico.
   */
  preventInput?: boolean;
}

// Si pasa este tiempo entre teclas, descartamos el buffer (era tipeo suelto).
const IDLE_RESET_MS = 500;

export function useBarcodeScanner(onScan: (code: string) => void, options: Options = {}) {
  const {
    enabled = true,
    maxIntervalMs = 60,
    minLength = 3,
    flushTimeoutMs = 120,
    preventInput = false,
  } = options;

  // Guardamos onScan en un ref para no re-suscribir el listener en cada render.
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!enabled) return;

    let buffer = '';
    let startTime = 0;
    let lastTime = 0;
    let flushTimer: ReturnType<typeof setTimeout> | undefined;

    // ¿El buffer parece una lectura de lector? (largo suficiente + rápido)
    const looksLikeScan = () => {
      if (buffer.length < minLength) return false;
      const perChar = buffer.length > 1 ? (lastTime - startTime) / (buffer.length - 1) : Infinity;
      return perChar <= maxIntervalMs;
    };

    const emitIfScan = () => {
      if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = undefined;
      }
      if (looksLikeScan()) {
        const code = buffer;
        buffer = '';
        onScanRef.current(code);
        return true;
      }
      buffer = '';
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignorar combinaciones con modificadores y auto-repeat (tecla mantenida).
      if (e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;

      const now = e.timeStamp || performance.now();
      const gap = now - lastTime;

      // Sufijos típicos de lector: cierran la lectura al instante.
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (looksLikeScan()) {
          // Evitar que el Enter/Tab del lector dispare submit o mueva el foco.
          e.preventDefault();
        }
        emitIfScan();
        return;
      }

      // Solo caracteres imprimibles de 1 char (dígitos/letras del código).
      if (e.key.length !== 1) return;

      // Buffer nuevo si es la primera tecla o si hubo una pausa larga (tipeo humano).
      if (!buffer || gap > IDLE_RESET_MS) {
        buffer = e.key;
        startTime = now;
      } else {
        buffer += e.key;
        // Ya vamos a velocidad de lector: mantenemos los caracteres fuera del
        // campo enfocado para no ensuciar otros inputs.
        if (preventInput && gap <= maxIntervalMs) e.preventDefault();
      }
      lastTime = now;

      // Cierre por pausa: si no llega otra tecla pronto, intentamos emitir.
      // Sirve para lectores que NO agregan Enter/Tab al final.
      if (flushTimer) clearTimeout(flushTimer);
      flushTimer = setTimeout(emitIfScan, flushTimeoutMs);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (flushTimer) clearTimeout(flushTimer);
    };
  }, [enabled, maxIntervalMs, minLength, flushTimeoutMs, preventInput]);
}
